import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { ref, get } from 'firebase/database';
import { Navbar, Nav, Container, Dropdown } from 'react-bootstrap';
import { useTheme } from '../context/ThemeContext';
import '../style/Header.css';
import Popup from './Popup';

const Header = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const navigate = useNavigate();
  const { isDark, toggleTheme, theme } = useTheme();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // Fetch user data from database
        try {
          const userRef = ref(db, `users/${currentUser.uid}`);
          const snapshot = await get(userRef);
          
          if (snapshot.exists()) {
            setUserData(snapshot.val());
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setUserData(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = async () => {
    try {
      await signOut(auth);
      setExpanded(false);
      setShowLogoutModal(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      setPopup({
        isOpen: true,
        title: 'Logout Failed',
        message: "Error logging out: " + error.message,
        type: 'error'
      });
    }
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleNavClick = (path) => {
    setExpanded(false);
    navigate(path);
  };

  const getRoleBadge = (role) => {
    if (role === 'organizer') {
      return <span className="badge bg-warning text-dark ms-2">Organizer</span>;
    } else if (role === 'bidder') {
      return <span className="badge bg-success ms-2">Bidder</span>;
    }
    return null;
  };

  return (
    <>
      <Navbar 
        bg="dark" 
        variant="dark" 
        expand="lg" 
        expanded={expanded}
        onToggle={setExpanded}
        className="header-navbar shadow-lg"
        sticky="top"
      >
        <Container fluid className="px-4">
          <Navbar.Brand 
            onClick={() => handleNavClick('/')} 
            className="brand-logo d-flex align-items-center"
            style={{ cursor: 'pointer' }}
          >
            <div className="logo-icon">
              <i className="bi bi-trophy-fill"></i>
            </div>
            <span style={{ color: theme.text, fontWeight: 'bold' }}
              className=''>SportsBid Arena</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="navbarNav">
            <i className="bi bi-list"></i>
          </Navbar.Toggle>
          <Navbar.Collapse id="navbarNav">
            <Nav className="ms-auto align-items-lg-center">
              {/* Theme Toggle Button */}
              <Nav.Item className="me-3">
                <button
                  onClick={toggleTheme}
                  className="btn btn-link text-white p-2 theme-toggle-btn"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-fill'} fs-5`}></i>
                </button>
              </Nav.Item>

              {user && userData ? (
                <>
                  {/* User Profile Dropdown */}
                  <Dropdown align="end" className="user-dropdown">
                    <Dropdown.Toggle 
                      variant="link" 
                      id="user-dropdown"
                      className="text-decoration-none text-white d-flex align-items-center nav-user-menu"
                    >
                      <div className="user-avatar me-2">
                        <i className="bi bi-person-circle"></i>
                      </div>
                      <div className="user-info d-none d-lg-block text-start">
                        <div className="user-name fw-bold">
                          {userData.name || 'User'}
                          {getRoleBadge(userData.role)}
                        </div>
                        <small className="user-email text-white-50">{user.email}</small>
                      </div>
                      <i className="bi bi-chevron-down ms-2"></i>
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="user-dropdown-menu shadow-lg">
                      <div className="dropdown-header-custom">
                        <div className="user-avatar-large mb-2">
                          <i className="bi bi-person-circle"></i>
                        </div>
                        <div className="fw-bold">{userData.name || 'User'}</div>
                        <small className="text-muted text-white-50">{user.email}</small>
                        {getRoleBadge(userData.role)}
                      </div>
                      <Dropdown.Divider />
                      <Dropdown.Item 
                        onClick={() => handleNavClick('/profile')}
                        className="dropdown-item-custom"
                      >
                        <i className="bi bi-person-fill me-2"></i>
                        My Profile
                      </Dropdown.Item>

                      {userData.role === 'organizer' && (
                        <Dropdown.Item 
                          onClick={() => handleNavClick('/organizer')}
                          className="dropdown-item-custom"
                        >
                          <i className="bi bi-speedometer2 me-2"></i>
                          Dashboard
                        </Dropdown.Item>
                      )}

                      {userData.role === 'bidder' && (
                        <Dropdown.Item 
                          onClick={() => handleNavClick('/bidder')}
                          className="dropdown-item-custom"
                        >
                          <i className="bi bi-speedometer2 me-2"></i>
                          Dashboard
                        </Dropdown.Item>
                      )}

                      <Dropdown.Divider />

                      <Dropdown.Item 
                        onClick={handleLogout}
                        className="dropdown-item-custom text-danger"
                      >
                        <i className="bi bi-box-arrow-right me-2"></i>
                        Sign Out
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </>
              ) : (
                <>
                  <Nav.Item className="mb-2 mb-lg-0 me-2">
                    <button 
                      onClick={() => handleNavClick('/sign-in')}
                      className="btn btn-outline-light btn-nav"
                    >
                      <i className="bi bi-box-arrow-in-right me-2"></i>
                      Sign In
                    </button>
                  </Nav.Item>
                  <Nav.Item>
                    <button 
                      onClick={() => handleNavClick('/sign-up')}
                      className="btn btn-primary btn-nav"
                    >
                      <i className="bi bi-person-plus me-2"></i>
                      Sign Up
                    </button>
                  </Nav.Item>
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="modal-overlay" onClick={cancelLogout}>
          <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div className="modal-icon">
                <i className="bi bi-exclamation-circle"></i>
              </div>
              <h4 className="mb-0">Confirm Sign Out</h4>
            </div>
            <div className="modal-body-custom">
              <p className="text-muted mb-0">
                Are you sure you want to sign out? You'll need to sign in again to access your account.
              </p>
            </div>
            <div className="modal-footer-custom">
              <button 
                onClick={cancelLogout}
                className="btn btn-secondary"
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </button>
              <button 
                onClick={confirmLogout}
                className="btn btn-danger"
              >
                <i className="bi bi-box-arrow-right me-2"></i>
                Yes, Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        type={popup.type}
        onClose={() => setPopup({ ...popup, isOpen: false })}
      />
    </>
  );
};

export default Header;